/**
 * shell/sidebar.tsx — the 248px app shell rail.
 * Brand + demo-tenant switcher, nav grouped Work / Author / Configure (active =
 * accent-soft), and the signed-in footer with a "Live" status chip. Renders
 * docked on wide screens and as an overlay drawer on mobile.
 */
import React, { useState } from 'react';
import { View, Pressable, ScrollView } from 'react-native';
import { LightColors as C, Radii as R, Shadows as SH, FontSize as FS, FontWeight as FW } from '../theme/theme';
import { Txt, Eyebrow } from '../components/ui';
import Icon, { IconName } from '../components/icon';
import { useSession, roleLabel } from '../context/sessionContext';
import { useCounts } from '../context/countsContext';
import { useActiveRoute } from '../navigation/useActiveRoute';
import { navigateApp } from '../navigation/navigationRef';
import type { AppRouteName } from '../navigation/types';

interface NavItem {
  route: AppRouteName;
  label: string;
  icon: IconName;
  count?: number;
  badge?: number;
}

interface NavGroup {
  group: string;
  items: NavItem[];
}

const NavButton: React.FC<{ item: NavItem; active: boolean; onPress: () => void }> = ({ item, active, onPress }) => {
  const [hover, setHover] = useState(false);
  const fg = active ? C.accentInk : hover ? C.ink : C.ink2;
  return (
    <View style={{ position: 'relative', marginVertical: 1 }}>
      {/* active accent rail — a quiet premium cue */}
      {active && (
        <View style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 999, backgroundColor: C.accentInk, zIndex: 1 }} />
      )}
      <Pressable
        onPress={onPress}
        onHoverIn={() => setHover(true)}
        onHoverOut={() => setHover(false)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: 11,
          paddingVertical: 9,
          paddingHorizontal: 11,
          borderRadius: R.r3,
          backgroundColor: active ? C.accentSoft : hover ? C.paper2 : 'transparent',
        }}
      >
        <Icon name={item.icon} size={17} color={fg} />
        <Txt size={FS.md} weight={active ? FW.semibold : FW.medium} color={fg} style={{ flex: 1 }} numberOfLines={1}>
          {item.label}
        </Txt>
      {item.count !== undefined && (
        <View
          style={{
            minWidth: 18,
            height: 18,
            paddingHorizontal: 5,
            borderRadius: 9,
            backgroundColor: C.accent,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Txt size={FS.xs} weight={FW.semibold} color="#FFFFFF">
            {item.count}
          </Txt>
        </View>
      )}
      {item.badge !== undefined && item.badge > 0 && (
        <View
          style={{
            minWidth: 18,
            height: 18,
            paddingHorizontal: 5,
            borderRadius: 9,
            backgroundColor: C.accent,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Txt size={FS.xs} weight={FW.semibold} color="#FFFFFF">
            {item.badge}
          </Txt>
        </View>
      )}
      </Pressable>
    </View>
  );
};

interface SidebarProps {
  onLogout: () => void;
  mobile?: boolean;
  onNavigate?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onLogout, mobile = false, onNavigate }) => {
  const { user, fullName, role, initials } = useSession();
  const { activeRuleCount, pendingCount } = useCounts();
  const active = useActiveRoute();
  const [idHover, setIdHover] = useState(false);
  const [outHover, setOutHover] = useState(false);

  const groups: NavGroup[] = [
    {
      group: 'Work',
      items: [
        { route: 'Rules', label: 'Rules', icon: 'rules-list', count: activeRuleCount },
        { route: 'Performance', label: 'Performance', icon: 'chart-line' },
      ],
    },
    {
      group: 'Author',
      items: [
        { route: 'Proposals', label: 'AI Proposals', icon: 'sparkle', badge: pendingCount },
        { route: 'RuleBuilder', label: 'Rule Builder', icon: 'pencil' },
      ],
    },
    { group: 'Configure', items: [{ route: 'Settings', label: 'Settings', icon: 'gear' }] },
  ];

  const go = (route: AppRouteName) => {
    navigateApp(route);
    onNavigate?.();
  };

  return (
    <View
      style={{
        width: 248,
        flexShrink: 0,
        backgroundColor: C.card,
        borderRightWidth: 1,
        borderRightColor: C.line,
        // Let the identity hover-card float past the rail, over the content.
        overflow: 'visible',
        zIndex: 30,
        ...(mobile ? (SH.lg as object) : {}),
      }}
    >
      {/* Brand — a clean premium wordmark */}
      <View
        style={{
          paddingHorizontal: 16,
          paddingTop: 20,
          paddingBottom: 18,
          borderBottomWidth: 1,
          borderBottomColor: C.line,
        }}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              backgroundColor: C.ink,
              alignItems: 'center',
              justifyContent: 'center',
              ...(SH.md as object),
            }}
          >
            <Txt size={14} weight={FW.bold} color="#FFFFFF" style={{ letterSpacing: 1 }}>
              SK
            </Txt>
          </View>
          <View style={{ flex: 1, minWidth: 0 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 5 }}>
              <Txt size={21} weight={FW.bold} color={C.ink} style={{ letterSpacing: 0.2 }}>
                Upsell
              </Txt>
              <View style={{ width: 5, height: 5, borderRadius: 3, backgroundColor: C.accent }} />
            </View>
            <Txt size={FS.xs} weight={FW.semibold} color={C.ink3} style={{ letterSpacing: 1, textTransform: 'uppercase', marginTop: 1 }}>
              Operator Console
            </Txt>
          </View>
        </View>
      </View>

      {/* Nav */}
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingHorizontal: 10, paddingVertical: 6 }}>
        {groups.map((g) => (
          <View key={g.group}>
            <Eyebrow style={{ paddingHorizontal: 10, paddingTop: 12, paddingBottom: 6 }}>{g.group}</Eyebrow>
            {g.items.map((item) => (
              <NavButton
                key={item.route}
                item={item}
                active={active === item.route}
                onPress={() => go(item.route)}
              />
            ))}
          </View>
        ))}

        {/* Account — Logout lives in the nav, styled like any nav item */}
        <View>
          <Eyebrow style={{ paddingHorizontal: 10, paddingTop: 12, paddingBottom: 6 }}>Account</Eyebrow>
          <Pressable
            onPress={onLogout}
            onHoverIn={() => setOutHover(true)}
            onHoverOut={() => setOutHover(false)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 11,
              paddingVertical: 9,
              paddingHorizontal: 11,
              borderRadius: R.r3,
              marginVertical: 1,
              backgroundColor: outHover ? C.paper2 : 'transparent',
            }}
          >
            <Icon name="logout" size={17} color={outHover ? C.ink : C.ink2} />
            <Txt size={FS.md} weight={outHover ? FW.semibold : FW.medium} color={outHover ? C.ink : C.ink2}>
              Logout
            </Txt>
          </Pressable>
        </View>
      </ScrollView>

      {/* Footer: premium identity — hover reveals a detail card */}
      <View style={{ borderTopWidth: 1, borderTopColor: C.line, padding: 10 }}>
        <View style={{ position: 'relative' }}>
          {idHover && (
            <View
              style={{
                position: 'absolute',
                left: 0,
                width: 320,
                bottom: '100%',
                marginBottom: 8,
                backgroundColor: C.card,
                borderWidth: 1,
                borderColor: C.line,
                borderRadius: R.r3,
                padding: 14,
                zIndex: 50,
                ...(SH.lg as object),
              }}
            >
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <View
                  style={{
                    width: 42,
                    height: 42,
                    borderRadius: 21,
                    backgroundColor: C.ink,
                    alignItems: 'center',
                    justifyContent: 'center',
                    ...(SH.sm as object),
                  }}
                >
                  <Txt size={15} weight={FW.bold} color="#FFFFFF" style={{ letterSpacing: 0.5 }}>
                    {initials || 'PM'}
                  </Txt>
                </View>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Txt size={FS.md} weight={FW.bold} color={C.ink} tracking={-0.2} transform="uppercase">
                    {fullName || 'Priya Menon'}
                  </Txt>
                  {role ? (
                    <Txt size={FS.sm} weight={FW.medium} color={C.ink3} style={{ marginTop: 1 }}>
                      {roleLabel(role)}
                    </Txt>
                  ) : null}
                </View>
              </View>
              {user?.email ? (
                <View style={{ marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.line }}>
                  <Txt size={9.5} weight={FW.semibold} color={C.ink4} style={{ letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>
                    Email
                  </Txt>
                  <Txt size={FS.sm} color={C.ink2} numberOfLines={1}>
                    {user.email}
                  </Txt>
                </View>
              ) : null}
            </View>
          )}
          <Pressable
            onHoverIn={() => setIdHover(true)}
            onHoverOut={() => setIdHover(false)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 12,
              paddingVertical: 8,
              paddingHorizontal: 8,
              borderRadius: R.r3,
              backgroundColor: idHover ? C.paper2 : 'transparent',
            }}
          >
            {/* Premium initials avatar — black disc, white bold initials, ring */}
            <View
              style={{
                width: 40,
                height: 40,
                borderRadius: 20,
                backgroundColor: C.ink,
                alignItems: 'center',
                justifyContent: 'center',
                borderWidth: 2,
                borderColor: idHover ? C.accent : C.card,
                ...(SH.sm as object),
              }}
            >
              <Txt size={15} weight={FW.bold} color="#FFFFFF" style={{ letterSpacing: 0.5 }}>
                {initials || 'PM'}
              </Txt>
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Txt size={15.5} weight={FW.bold} color={C.ink} numberOfLines={1} tracking={-0.2} transform="uppercase">
                {fullName || 'Priya Menon'}
              </Txt>
            </View>
          </Pressable>
        </View>
      </View>
    </View>
  );
};

export default Sidebar;
